import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { Image } from "@/components/Image";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { cn } from "@/utils/helpers";
import { recentPointer } from "@/lib/pointerOrigin";
import { useBrandUniverseGraph } from "@/app/routes/dashboard/api/useBrandUniverseGraph";
import { useMarketSignals } from "@/app/routes/dashboard/market-signals/api/useMarketSignals";
import { useStrategyDetail } from "@/app/routes/dashboard/strategy/api/useStrategies";
import { useTrendDetail } from "@/app/routes/dashboard/trends/api/useTrends";
import type {
  WorkshopContextItem,
  WorkshopContextItemKind,
  WorkshopFile,
} from "@/types/workshop.type";
import { useWorkshopFileUrl, type WorkshopFileUrl } from "../api/useWorkshopFiles";
import type { WorkshopMediaItem } from "../api/useWorkshopMedia";
import { formatAttribution, useViewerId } from "../attribution";
import { formatMadeDate } from "../workshopOutputs";
import { usePermissions } from "@/hooks/usePermissions";
import WorkshopDialog from "./WorkshopDialog";
import { KitVideoPlayer } from "@/app/routes/dashboard/components/kit/VideoPlayer";

export type SourcePreviewTarget =
  | { kind: "file"; file: WorkshopFile }
  | { kind: "context"; item: WorkshopContextItem }
  | { kind: "picture"; item: WorkshopMediaItem };

const FILE_TYPE_WORDS: Record<string, string> = {
  pdf: "PDF",
  video: "Video",
  spreadsheet: "Spreadsheet",
  text: "Text file",
};

/** "Spreadsheet", "Video": what a project file is, in a word. */
export function fileTypeWord(type: string): string {
  return FILE_TYPE_WORDS[type] ?? "File";
}

const CONTEXT_KIND_WORDS: Record<WorkshopContextItemKind, string> = {
  universe_fact: "Universe fact",
  strategy: "Strategy",
  market_signal: "Market signal",
  trend: "Trend",
  document: "Document",
  url: "Link",
};

/** What a pinned item is, in the singular ("Universe fact"). */
export function contextKindWord(kind: string): string {
  return CONTEXT_KIND_WORDS[kind as WorkshopContextItemKind] ?? "Pinned item";
}

type Props = {
  brandId: string;
  projectId: string;
  target: SourcePreviewTarget | null;
  onClose: () => void;
};

function PreviewFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[160px] flex-col gap-3">
      {children}
    </div>
  );
}

function PreviewStatus({ label }: { label: string }) {
  return (
    <div className="flex min-h-[160px] items-center justify-center gap-2 text-co-faint">
      <Loader2 className="size-4 animate-spin" />
      <span className="tt-read-s">{label}</span>
    </div>
  );
}

function PreviewEmpty({ children }: { children: React.ReactNode }) {
  return <p className="tt-read-s text-co-muted">{children}</p>;
}

/** How much of a text file the preview shows. */
const TEXT_PREVIEW_BYTES = 200_000;

/**
 * The start of a text file, read off the stream and then cancelled, so a large
 * upload is never pulled into the tab whole.
 */
async function readTextHead(url: string): Promise<{ text: string; truncated: boolean }> {
  const res = await fetch(url);
  if (!res.ok || !res.body) throw new Error(`Couldn't load the file (${res.status})`);
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  let truncated = false;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    size += value.byteLength;
    if (size >= TEXT_PREVIEW_BYTES) {
      truncated = true;
      void reader.cancel();
      break;
    }
  }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  // Always UTF-8: the upload re-encodes anything else (see prepareDataFile).
  return { text: new TextDecoder().decode(bytes.subarray(0, TEXT_PREVIEW_BYTES)), truncated };
}

function prettyJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

/** RFC 4180-ish: quoted fields, escaped quotes, commas and newlines inside quotes. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (quoted) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      quoted = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i += 1;
      row.push(cell);
      cell = "";
      rows.push(row);
      row = [];
    } else {
      cell += char;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  const last = rows[rows.length - 1];
  if (last && last.length <= 1 && (last[0] ?? "") === "") rows.pop();
  return rows;
}

function CsvCells({ text }: { text: string }) {
  const rows = parseCsv(text);
  if (rows.length === 0) return <PreviewEmpty>This file is empty.</PreviewEmpty>;
  const columnCount = rows.reduce((count, row) => Math.max(count, row.length), 0);
  const [header, ...body] = rows;
  const cellClass =
    "min-w-16 max-w-70 border border-co-line px-2 py-1.5 align-top wrap-break-word";
  return (
    <div className="max-h-[55dvh] w-full overflow-auto rounded-card border border-co-line bg-co-surface">
      <table className="w-max min-w-full border-collapse text-left">
        <thead>
          <tr>
            {Array.from({ length: columnCount }, (_, index) => (
              <th
                key={index}
                className={`tt-label sticky top-0 bg-co-surface-solid text-left font-semibold text-co-text ${cellClass}`}
              >
                {header[index] ?? ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columnCount }, (_, cellIndex) => (
                <td key={cellIndex} className={`tt-read-s text-co-text2 ${cellClass}`}>
                  {row[cellIndex] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TextFilePreview({
  url,
  mimeType,
  asCells,
  mono,
}: {
  url: string;
  mimeType: string | null;
  asCells?: boolean;
  mono?: boolean;
}) {
  const query = useQuery({
    queryKey: ["workshopFileText", url],
    queryFn: () => readTextHead(url),
    staleTime: Infinity,
    retry: false,
  });
  if (query.isLoading) return <PreviewStatus label="Loading file…" />;
  if (query.isError || !query.data) {
    return <PreviewEmpty>Couldn't load a preview of this file.</PreviewEmpty>;
  }
  const { text, truncated } = query.data;
  if (asCells) {
    return (
      <>
        <CsvCells text={text} />
        {truncated ? (
          <p className="tt-label text-co-faint">Showing the start of the file.</p>
        ) : null}
      </>
    );
  }
  const shown = mimeType === "application/json" && !truncated ? prettyJson(text) : text;
  return (
    <>
      <pre
        className={`${mono ? "tt-data overflow-auto whitespace-pre" : "tt-read-s overflow-auto whitespace-pre-wrap wrap-break-word"} max-h-[55dvh] w-full min-w-0 rounded-card bg-co-surface p-3 text-co-text2`}
      >
        {shown || "This file is empty."}
      </pre>
      {truncated ? (
        <p className="tt-label text-co-faint">Showing the start of the file.</p>
      ) : null}
    </>
  );
}

function FilePreview({
  file,
  query,
}: {
  file: WorkshopFile;
  query: UseQueryResult<WorkshopFileUrl>;
}) {
  if (query.isLoading) return <PreviewStatus label="Loading file…" />;
  if (query.isError || !query.data?.url) {
    return <PreviewEmpty>Couldn't load this file.</PreviewEmpty>;
  }

  const { url, mimeType, type } = query.data;
  const isVideo = type === "video" || (mimeType ?? "").startsWith("video/");
  const isSheet =
    type === "spreadsheet" ||
    mimeType === "text/csv" ||
    (file.fileName ?? "").toLowerCase().endsWith(".csv");
  const isJson =
    mimeType === "application/json" || (file.fileName ?? "").toLowerCase().endsWith(".json");
  const isText = type === "text" || isSheet;

  return (
    <PreviewFrame>
      {isVideo ? (
        <KitVideoPlayer src={url} label={file.fileName ?? "Video"} maxHeight="55dvh" />
      ) : isText ? (
        <TextFilePreview url={url} mimeType={mimeType} asCells={isSheet} mono={isJson} />
      ) : (
        <iframe
          title={file.fileName ?? "PDF"}
          src={url}
          className="h-[55dvh] w-full rounded-card bg-co-surface"
        />
      )}
    </PreviewFrame>
  );
}

/** A project picture, whole, with what its record says about it. */
function PicturePreview({ item }: { item: WorkshopMediaItem }) {
  return (
    <PreviewFrame>
      <div className="ws-preview-picture">
        <Image
          src={item.url ?? undefined}
          alt={item.fileName}
          className="max-h-[55dvh] w-auto max-w-full object-contain"
        />
      </div>
      {item.isAiGenerated && item.prompt ? (
        <p className="tt-read-s text-co-muted">{item.prompt}</p>
      ) : null}
    </PreviewFrame>
  );
}

function FactPreview({ brandId, targetId }: { brandId: string; targetId: string }) {
  const graph = useBrandUniverseGraph(brandId);
  if (graph.isLoading) return <PreviewStatus label="Loading fact…" />;
  const fact = graph.data?.nodes.find((n) => n.type === "fact" && n.id === targetId);
  if (!fact || fact.type !== "fact") {
    return <PreviewEmpty>This fact is no longer in the brand universe.</PreviewEmpty>;
  }
  return (
    <PreviewFrame>
      <p className="tt-read-m whitespace-pre-wrap text-co-text2">{fact.description}</p>
      {fact.entityName ? (
        <p className="tt-label uppercase text-co-faint">{fact.entityName}</p>
      ) : null}
    </PreviewFrame>
  );
}

function StrategyPreview({ brandId, targetId }: { brandId: string; targetId: string }) {
  const { detail, isLoading, isError } = useStrategyDetail(brandId, targetId);
  if (isLoading) return <PreviewStatus label="Loading strategy…" />;
  if (isError || !detail) {
    return <PreviewEmpty>This strategy could not be loaded.</PreviewEmpty>;
  }
  const { strategy } = detail;
  const objective = strategy.document?.brief?.business_objective?.line ?? strategy.document?.brief?.social_objective?.text ?? null;
  return (
    <PreviewFrame>
      <p className="tt-label uppercase text-co-faint">
        {strategy.version} · {strategy.status}
      </p>
      {objective ? (
        <p className="tt-read-m whitespace-pre-wrap text-co-text2">{objective}</p>
      ) : (
        <PreviewEmpty>No objective written on this strategy.</PreviewEmpty>
      )}
    </PreviewFrame>
  );
}

function SignalPreview({ brandId, targetId }: { brandId: string; targetId: string }) {
  const { signals, isLoading } = useMarketSignals(brandId);
  if (isLoading) return <PreviewStatus label="Loading signal…" />;
  const signal = (signals ?? []).find((s) => s.id === targetId);
  if (!signal) return <PreviewEmpty>This market signal is no longer available.</PreviewEmpty>;
  return (
    <PreviewFrame>
      <p className="tt-label uppercase text-co-faint">
        {signal.kind} · {signal.status}
      </p>
      <p className="tt-read-m whitespace-pre-wrap text-co-text2">{signal.body}</p>
    </PreviewFrame>
  );
}

function TrendPreview({ targetId }: { targetId: string }) {
  const query = useTrendDetail(targetId);
  if (query.isLoading) return <PreviewStatus label="Loading trend…" />;
  const trend = query.data;
  if (query.isError || !trend) {
    return <PreviewEmpty>This trend could not be loaded.</PreviewEmpty>;
  }
  const thumb = trend.previewVideo?.thumbnailUrl ?? trend.examples[0]?.thumbnailUrl ?? null;
  const videoUrl = trend.previewVideo?.videoUrl ?? null;
  return (
    <PreviewFrame>
      {videoUrl ? (
        // A trend is a vertical short: the frame holds 9:16 until the video says.
        <KitVideoPlayer src={videoUrl} poster={thumb} ratio={9 / 16} label={trend.title} maxHeight="40dvh" />
      ) : thumb ? (
        <Image
          src={thumb}
          alt=""
          className="max-h-[40dvh] w-full rounded-card object-contain"
        />
      ) : null}
      <p className="tt-read-m whitespace-pre-wrap text-co-text2">{trend.body}</p>
    </PreviewFrame>
  );
}

function UrlPreview({ url, note }: { url: string; note: string | null }) {
  return (
    <PreviewFrame>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="tt-read-m inline-flex items-center gap-1.5 break-all text-co-accent-ink hover:underline"
      >
        {url}
        <ExternalLink className="size-3 shrink-0" aria-hidden />
      </a>
      {note ? <p className="tt-read-s text-co-muted">{note}</p> : null}
    </PreviewFrame>
  );
}

function ContextPreview({
  brandId,
  item,
}: {
  brandId: string;
  item: WorkshopContextItem;
}) {
  const { isGuest } = usePermissions();
  if (item.kind === "url" && item.url) {
    return <UrlPreview url={item.url} note={item.note} />;
  }
  if (!item.targetId) {
    return item.note ? (
      <PreviewFrame>
        <p className="tt-read-s text-co-muted">{item.note}</p>
      </PreviewFrame>
    ) : (
      <PreviewEmpty>Nothing to preview for this source.</PreviewEmpty>
    );
  }
  if (item.kind === "universe_fact") {
    return <FactPreview brandId={brandId} targetId={item.targetId} />;
  }
  // Strategy and signals are team surfaces: a guest sees the source is there
  // (and the model still reads it), but not its body.
  if ((item.kind === "strategy" || item.kind === "market_signal") && isGuest) {
    return item.note ? (
      <PreviewFrame>
        <p className="tt-read-s text-co-muted">{item.note}</p>
      </PreviewFrame>
    ) : (
      <PreviewEmpty>Your team added this source. Its details are only visible to them.</PreviewEmpty>
    );
  }
  if (item.kind === "strategy") {
    return <StrategyPreview brandId={brandId} targetId={item.targetId} />;
  }
  if (item.kind === "market_signal") {
    return <SignalPreview brandId={brandId} targetId={item.targetId} />;
  }
  if (item.kind === "trend") {
    return <TrendPreview targetId={item.targetId} />;
  }
  return item.note ? (
    <PreviewFrame>
      <p className="tt-read-s text-co-muted">{item.note}</p>
    </PreviewFrame>
  ) : (
    <PreviewEmpty>Nothing to preview for this source.</PreviewEmpty>
  );
}

/** The line under the title: what it is, where it came from, and who can read it. */
function previewLine(target: SourcePreviewTarget, viewerId: string | null): string {
  if (target.kind === "file") {
    const f = target.file;
    const by = formatAttribution(f.attribution?.createdBy, viewerId, { includeContext: false });
    const how = f.sourceUrl ? "attached from a link" : "added";
    return `${fileTypeWord(f.type)} · ${how} ${formatMadeDate(f.createdAt)}${by ? ` ${by}` : ""}. Every chat in this project can read it.`;
  }
  if (target.kind === "picture") {
    const m = target.item;
    const by = formatAttribution(m.attribution?.createdBy, viewerId, { includeContext: false });
    const parts = [
      m.isAiGenerated ? "Generated picture" : "Picture",
      m.width && m.height ? `${m.width}x${m.height}` : null,
      `${Math.max(1, Math.round(m.sizeBytes / 1024))} KB`,
      `added ${formatMadeDate(m.linkedAt)}${by ? ` ${by}` : ""}`,
    ].filter(Boolean);
    return `${parts.join(" · ")}. ${m.library ? "Also in the brand library." : "In this project only, not in the brand library."}`;
  }
  const item = target.item;
  const by = formatAttribution(item.attribution?.createdBy, viewerId, { includeContext: false });
  return `${contextKindWord(item.kind)} · pinned ${formatMadeDate(item.createdAt)}${by ? ` ${by}` : ""}. Loaded on every turn, by the chat and by deployed agents.`;
}

export default function SourcePreview({ brandId, projectId, target: live, onClose }: Props) {
  const viewerId = useViewerId();
  // Closing clears `live` at once; the last target stays on screen for the
  // close animation, so the body does not vanish while the header shrinks.
  const [kept, setKept] = useState(live);
  if (live && live !== kept) setKept(live);
  const target = live ?? kept;

  // The dialog grows out of the row that was pressed to open it: found once
  // from the press point (the row under the dialog), then kept to close into.
  const cardRef = useRef<Element | null>(null);
  const growFrom = useCallback(() => {
    if (!cardRef.current?.isConnected) {
      cardRef.current = null;
      const press = recentPointer();
      if (press) {
        const under = document
          .elementsFromPoint(press.x, press.y)
          .find((el) => !el.closest(".co-modal-overlay"));
        cardRef.current = under?.closest("li") ?? null;
      }
    }
    return cardRef.current;
  }, []);
  useLayoutEffect(() => {
    if (!live) cardRef.current = null;
  }, [live]);

  const fileUrl = useWorkshopFileUrl(projectId, target?.kind === "file" ? target.file.id : null);
  const title =
    target?.kind === "file"
      ? (target.file.fileName ?? fileTypeWord(target.file.type))
      : target?.kind === "picture"
        ? target.item.fileName
        : (target?.item.label ?? "Source");
  const href =
    target?.kind === "file"
      ? (fileUrl.data?.url ?? null)
      : target?.kind === "picture"
        ? target.item.url
        : target?.kind === "context" && target.item.kind === "url"
          ? target.item.url
          : null;
  const wide = target?.kind === "file" && target.file.type === "spreadsheet";

  return (
    <WorkshopDialog
      isOpen={live !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      growFrom={growFrom}
      title={title}
      description={target ? previewLine(target, viewerId) : undefined}
      className={cn("ws-preview", wide && "ws-preview--wide")}
      actions={
        <>
          {href ? (
            <a href={href} target="_blank" rel="noreferrer" className="ws-outline-btn ws-preview-open">
              <ExternalLink className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
              Open in a new tab
            </a>
          ) : null}
          <KitButton type="button" variant="secondary" className="ws-dialog-btn" onClick={onClose}>
            Done
          </KitButton>
        </>
      }
    >
      {target?.kind === "file" ? (
        <FilePreview file={target.file} query={fileUrl} />
      ) : target?.kind === "picture" ? (
        <PicturePreview item={target.item} />
      ) : target?.kind === "context" ? (
        <ContextPreview brandId={brandId} item={target.item} />
      ) : null}
    </WorkshopDialog>
  );
}
